import { Avatar, useTheme } from '@material-ui/core';
import { ArrowUpwardRounded, DeleteRounded } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { usePacuV2State } from '@/components/pages/kiosk/PACUv2/PacuV2State';
import gsap from 'gsap';
import { useMutation } from '@apollo/client';
import { unassignBed } from '@/graph/patients';

const PACUv2SectionSeparator = ({ roomId }: { roomId: any }) => {
  const theme = useTheme();
  const classes = useStyles();
  const [state, setState] = usePacuV2State();
  const [text, setText] = React.useState('Select OR');

  const selected = state?.type === 'AssignBed';

  const arrowRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  const [unassignBedMutation, { loading }] = useMutation(unassignBed);

  const handleRemove = async () => {
    try {
      await unassignBedMutation({
        variables: {
          // @ts-ignore
          id: state?.patientId,
          // @ts-ignore
          bed: state?.patientPacuBed,
          roomId: roomId,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    gsap.set(lineRef.current, { width: 50, height: '4', backgroundColor: theme.palette.primary.main });
    gsap.set(rootRef.current, { width: 'auto', height: '4', backgroundColor: theme.palette.background.paper });
    gsap.set(bodyRef.current, { backgroundColor: theme.palette.background.paper });
    gsap.set(arrowRef.current, { rotate: 0 });

    timelineRef.current = gsap.timeline({ paused: true });

    const tl = timelineRef.current;

    if (bodyRef.current) {
      tl.to(bodyRef.current, {
        backgroundColor: theme.palette.primary.main,
        duration: 0.2,
      }).to(lineRef.current, {
        width: '100%',
        duration: 0.2,
        ease: 'power4.out',
      });

      tl.to(textRef.current, {
        opacity: 0,
        duration: 0.2,
        onComplete: () => {
          setText('Choose bed');
          gsap.to(bodyRef.current, { width: 'auto', duration: 0.2 });
          gsap.to(textRef.current, { opacity: 1, duration: 0.2 });
        },
      });

      tl.to(arrowRef.current, {
        rotate: 180,
        duration: 0.2,
      });
    }
    return () => {
      timelineRef.current?.kill();
    };
  }, [theme.palette.background.paper, theme.palette.primary.main]);

  useEffect(() => {
    if (!timelineRef.current) return;
    if (selected) {
      timelineRef.current.play();
    } else {
      timelineRef.current.reverse();
      if (textRef.current) {
        gsap.to(textRef.current, {
          opacity: 0,
          duration: 0.2,
          onComplete: () => {
            setText('Select OR');
            gsap.to(textRef.current, { opacity: 1, duration: 0.2 });
          },
        });
      }
    }
  }, [selected]);

  return (
    <div className={classes.root}>
      <div className={classes.lineBase}></div>
      <div ref={lineRef} className={classes.line}></div>
      <div ref={bodyRef} className={classes.body}>
        <Avatar ref={arrowRef} className={classes.avatar}>
          <ArrowUpwardRounded />
        </Avatar>
        <Typography variant="subtitle1">
          <span ref={textRef}>{text}</span>
        </Typography>
      </div>
      {selected && state?.patientPacuBed && (
        <div onClick={handleRemove} className={classes.removeContainer} role="button" tabIndex={0}>
          <DeleteRounded className={classes.removeIcon} />
          <Typography variant="subtitle1" className={classes.removeText}>
            Remove Bed
          </Typography>
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
  },
  lineBase: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: 4,
    backgroundColor: theme.palette.background.paper,
    zIndex: -1,
  },
  line: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 50,
    height: 4,
    backgroundColor: theme.palette.background.paper,
    zIndex: -1,
  },
  divider: {
    height: theme.spacing(1.5),
  },
  avatar: {
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
  },
  body: {
    backgroundColor: theme.palette.background.paper,
    paddingRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    borderRadius: theme.spacing(10),
  },
  removeContainer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    borderRadius: theme.spacing(10),
    marginLeft: theme.spacing(1),
    border: `1px solid ${theme.palette.error.main}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.error.light,
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.error.main}`,
      outlineOffset: '2px',
    },
  },
  removeIcon: {
    color: theme.palette.error.main,
    fontSize: '1.25rem',
  },
  removeText: {
    color: theme.palette.error.main,
  },
}));

export default PACUv2SectionSeparator;
